import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { type ReactNode, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import {
  ATTENTION_STATUS_BITS,
  AttentionSeverityPlate,
  getAttentionStatusRole,
  type IRaisedAttentionStatus,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { selectCockpitOffering, selectCockpitUserAccess } from '../../../../model/selectors'
import {
  CockpitCardCornerMenuButton,
  CockpitCardCornerSlotProvider,
  type ICockpitCardCornerMenuItem,
  useCockpitCardCornerHost,
} from '../../../components/cockpitCardCorner'
import { AttentionCardPartnerProvider } from '../../hooks/useAttentionCardPartner'
import { AttentionDetailSeverityProvider } from '../../hooks/useAttentionDetailSeverity'
import {
  AttentionIntroFootSlotProvider,
  AttentionIntroHeadSlotProvider,
  AttentionIntroSlotProvider,
} from '../../hooks/useAttentionIntroSlot'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import type { IAttentionStatusesCrumb, IAttentionStatusesItem } from '../../types'
import { AttentionStatusActions } from '../attentionStatusActions'
import { AttentionStatusesBreadcrumb } from '../attentionStatusesBreadcrumb'

import styles from './AttentionStatusPlaceholder.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail
const COMPLETENESS_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.completeness

const SEVERITY_DOT_SIZE = 10
const RELATED_COUNT_SIZE = 18

interface IAttentionStatusPlaceholderProps {
  status: IRaisedAttentionStatus
  /** Levels walked into before this one; empty in the single-partner cockpit. */
  crumbs?: IAttentionStatusesCrumb[]
  /** Partner the manager cockpit drilled down to. */
  partnerName?: string
  /** That partner's id: the card's action menu offers the way to their own plan page. */
  partnerId?: string
  /** The subject's other raised statuses, offered as one-click switches; empty draws no panel. */
  relatedStatuses?: IAttentionStatusesItem[]
  /** The status' own detail section, where one is written; it stands in place of the facts. */
  section?: ReactNode
  onBack: () => void
  /** Jump to a level of the breadcrumb; `-1` is the status list. */
  onNavigate?: (index: number) => void
  onSelectRelated?: (id: string) => void
}

/**
 * A status' detail. Statuses that already have a section of their own render it through `section`;
 * the rest stand in with what the widget knows about the picked one. The trail on top carries the
 * severity and the way back, so the detail itself opens straight on its content.
 * `relatedStatuses` adds the subject's other statuses: a panel in the completeness text column,
 * and a button in the corner everywhere else.
 */
export const AttentionStatusPlaceholder = ({
  status,
  crumbs = [],
  partnerName,
  partnerId,
  relatedStatuses = [],
  section,
  onBack,
  onNavigate,
  onSelectRelated,
}: IAttentionStatusPlaceholderProps) => {
  const text = useAttentionStatusesText()
  const t = useSafetyTranslation()
  const { container: corner, setContainer: setCorner } = useCockpitCardCornerHost()
  const [introEnd, setIntroEnd] = useState<HTMLElement | null>(null)
  const [introFoot, setIntroFoot] = useState<HTMLElement | null>(null)
  const [introHead, setIntroHead] = useState<HTMLElement | null>(null)
  const { role } = useSelector(selectCockpitUserAccess)
  /* A distributor's plan is signed by its DBM, so the lead sentence names the DBM. */
  const { isDistributor } = useSelector(selectPartnerAccess)

  const hasRelated = relatedStatuses.length > 0 && Boolean(onSelectRelated)

  /* On `Overall` the completeness section lists every programme, so its lead sentence says so
     rather than speaking of one offering. */
  const isOverallCompleteness =
    useSelector(selectCockpitOffering) === ProgramTypes.Overall &&
    status.bitValue === ATTENTION_STATUS_BITS.planned

  /* One offering's completeness reads in two columns: the plan on the left at the width its bars
     read at, and on the right everything to read — the lead text, whatever the section closes it
     with, and the other statuses — so a long text never pushes the table down. */
  const isCompletenessSide =
    Boolean(section) && status.bitValue === ATTENTION_STATUS_BITS.planned && !isOverallCompleteness
  /* The SOB categories read the same way: the quarters table on the left at the width it needs,
     and on the right who the figures wait for, then the lead text. Its other statuses stay in the
     corner, where every detail but completeness keeps them. */
  const isSobSide = Boolean(section) && status.bitValue === ATTENTION_STATUS_BITS.sobActuals
  const isSideIntro = isCompletenessSide || isSobSide

  /* Every opened status says what it is showing before it shows it — the sections differ, the lead
     sentence is the one thing all of them have. */
  const intro = (
    <p className={cn(styles.intro, { [styles.introSide]: isSideIntro })}>
      {isOverallCompleteness ? (
        <Translation span text={COMPLETENESS_KEYS.overallIntro} />
      ) : (
        <Translation
          span
          {...text.phrase.introText(status.bitValue, getAttentionStatusRole(role), isDistributor)}
        />
      )}
      {/* Where a section closes the sentence with a way to what it is about. */}
      <span ref={setIntroEnd} className={styles.introEnd} />
    </p>
  )

  const relatedTitle = (
    <Translation span text={partnerName ? KEYS.alsoRaisedForPartner : KEYS.alsoRaised} />
  )

  /* The same plate the trail above carries, so a level looks alike wherever it is met. A list of
     them would beat as one, so only the trail's own pulses. */
  const renderRelatedRow = (item: IAttentionStatusesItem) => (
    <>
      <AttentionSeverityPlate
        className={styles.relatedSeverity}
        severity={item.severity}
        pulse={false}
      >
        <Translation span {...text.phrase.severityName(item.severity)} />
      </AttentionSeverityPlate>
      <span className={styles.relatedLabel}>{item.title}</span>
    </>
  )

  const relatedPanel = hasRelated && onSelectRelated && (
    <aside className={styles.related}>
      <span className={styles.relatedTitle}>{relatedTitle}</span>
      {relatedStatuses.map((item) => (
        <button
          key={item.id}
          type="button"
          className={styles.relatedItem}
          onClick={() => onSelectRelated(item.id)}
        >
          {renderRelatedRow(item)}
        </button>
      ))}
    </aside>
  )

  /* Beside a section the panel's column would squeeze it — the acknowledgment columns wrapped and
     ran under their own footer — so outside the completeness text column the other statuses wait
     in the corner, counted on the plate of the worst of them. */
  const relatedMenuItems: ICockpitCardCornerMenuItem[] = [
    {
      id: 'title',
      content: (
        <span className={cn(styles.relatedTitle, styles.relatedMenuTitle)}>{relatedTitle}</span>
      ),
    },
    ...relatedStatuses.map((item) => ({
      id: item.id,
      label: <span className={styles.relatedMenuRow}>{renderRelatedRow(item)}</span>,
      onClick: () => onSelectRelated?.(item.id),
    })),
  ]

  const relatedMenu = hasRelated && !isCompletenessSide && (
    <CockpitCardCornerMenuButton
      label={<Translation span text={KEYS.alsoRaised} />}
      ariaLabel={t(partnerName ? KEYS.alsoRaisedForPartner : KEYS.alsoRaised)}
      badge={
        <AttentionSeverityPlate
          className={styles.relatedCount}
          severity={Math.max(...relatedStatuses.map(({ severity }) => severity))}
          size={RELATED_COUNT_SIZE}
          pulse={false}
        >
          {relatedStatuses.length}
        </AttentionSeverityPlate>
      }
      items={relatedMenuItems}
    />
  )

  return (
    <CockpitCardCornerSlotProvider container={corner}>
      <AttentionIntroSlotProvider container={introEnd}>
        <AttentionIntroFootSlotProvider container={introFoot}>
          <AttentionIntroHeadSlotProvider container={introHead}>
            <AttentionDetailSeverityProvider value={status.severity}>
              <AttentionCardPartnerProvider partnerId={partnerId} partnerName={partnerName}>
                <div className={styles.root}>
                  <div className={styles.breadcrumb}>
                    <AttentionStatusesBreadcrumb
                      crumbs={crumbs}
                      current={partnerName ?? status.title}
                      /* The level the status stands at, as a dot in its colour beside the name it
                     belongs to; the word is left to assistive technology. */
                      currentBadge={
                        <AttentionSeverityPlate
                          severity={status.severity}
                          size={SEVERITY_DOT_SIZE}
                          pulse={false}
                          className={styles.severity}
                        >
                          <span className={styles.srOnly}>
                            <Translation span {...text.phrase.severityName(status.severity)} />
                          </span>
                        </AttentionSeverityPlate>
                      }
                      onNavigate={onNavigate ?? (() => onBack())}
                    />

                    {/* The corner closes the row: the one place every status keeps what its reader
                    may do. */}
                    <div className={styles.corner}>
                      {relatedMenu}
                      <div ref={setCorner} className={styles.actionsSlot} />
                    </div>
                  </div>

                  {isSideIntro ? (
                    <div className={styles.sideLayout}>
                      <div className={cn(styles.sideGrid, { [styles.sideGridWide]: isSobSide })}>
                        <div className={cn(styles.main, styles.mainWithSection)}>{section}</div>

                        <div className={styles.side}>
                          <div ref={setIntroHead} className={styles.introHead} />
                          {intro}
                          <div ref={setIntroFoot} className={styles.introFoot} />
                          {isCompletenessSide && relatedPanel}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {intro}

                      <div className={styles.body}>
                        <div
                          className={cn(styles.main, {
                            [styles.mainWithSection]: Boolean(section),
                          })}
                        >
                          {section ?? (
                            <>
                              <dl className={styles.facts}>
                                <dt>
                                  <Translation span text={KEYS.status} />
                                </dt>
                                <dd>{status.title}</dd>
                                {partnerName && (
                                  <>
                                    <dt>
                                      <Translation span text={KEYS.partner} />
                                    </dt>
                                    <dd>{partnerName}</dd>
                                  </>
                                )}
                                <dt>
                                  <Translation span text={KEYS.condition} />
                                </dt>
                                <dd className={styles.mono}>{status.condition || '--'}</dd>
                                {status.score && (
                                  <>
                                    <dt>
                                      <Translation span text={KEYS.score} />
                                    </dt>
                                    <dd className={styles.mono}>
                                      <Translation
                                        span
                                        {...text.phrase.score(
                                          status.score.value,
                                          status.score.target
                                        )}
                                      />
                                    </dd>
                                  </>
                                )}
                              </dl>

                              <p className={styles.note}>
                                <Translation span text={KEYS.note} />
                              </p>
                            </>
                          )}

                          {/* A section that draws its own bar closes the card itself; everything else
                          gets the bar here, so no opened status is left without what its reader
                          may do about it. */}
                          {!section && <AttentionStatusActions bitValue={status.bitValue} />}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </AttentionCardPartnerProvider>
            </AttentionDetailSeverityProvider>
          </AttentionIntroHeadSlotProvider>
        </AttentionIntroFootSlotProvider>
      </AttentionIntroSlotProvider>
    </CockpitCardCornerSlotProvider>
  )
}

AttentionStatusPlaceholder.displayName = 'AttentionStatusPlaceholder'
